import { useI18n } from '@lib/i18n'
import isFunction from 'lodash/isFunction'
import ReactMarkdown from 'react-markdown'
import { Platform } from 'react-native'
import remarkGfm from 'remark-gfm'
import styled from 'styled-components/native'
import { InlineLink, Text } from '../Elements/Text'
import { Heading, InlineImage, List, MarkdownContext, MarkdownNode, MarkdownProps, Table } from './MarkdownComponents'

const ListWrapper = styled.View`
  margin-bottom: 30px;
  padding-right: 20px;
`

export function Markdown({
  text,
  i18nEnabled = true,
  withoutParagraphSpacing,
  textStyle,
  listStyle,
  isCentered = false,
  customActions,
}: MarkdownProps) {
  const interpolateText = useI18n()
  const styles: Pick<MarkdownProps, 'textStyle' | 'listStyle'> = {
    textStyle: {
      weight: 'regular',
      boldWeight: 'semiBold',
      fontSize: 'medium',
      ...textStyle,
    },
    listStyle: {
      weight: 'medium',
      boldWeight: 'bold',
      fontSize: 'medium',
      ...listStyle,
    },
  }

  return (
    <MarkdownContext value={{ customActions, ...styles }}>
      <ReactMarkdown
        allowedElements={[
          'a',
          'h1',
          'h2',
          'h3',
          'h4',
          'h5',
          'h6',
          'p',
          'em',
          'strong',
          'ol',
          'ul',
          'li',
          'table',
          'tbody',
          'td',
          'th',
          'thead',
          'tr',
          'img',
          'code',
        ]}
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ children, href = '' }) => {
            // keep custom action keys as single characters to avoid hyphenation
            const onPress = isFunction(customActions?.[href]) ? customActions[href] : undefined

            return (
              <InlineLink color={styles.textStyle?.linkColor} onPress={onPress} href={href} size={textStyle?.fontSize}>
                {children}
              </InlineLink>
            )
          },
          p: ({ children }) => (
            <Text
              isCentered={isCentered}
              weight={styles.textStyle?.weight}
              size={styles.textStyle?.fontSize}
              color={styles.textStyle?.color}
            >
              {children}
              {withoutParagraphSpacing ? null : Platform.select({ native: '\n', web: '\n\n' })}
            </Text>
          ),
          h1: ({ children, ...rest }) => {
            console.log(rest)
            return <Heading level={1}>{children}</Heading>
          },
          h2: ({ children }) => <Heading level={2}>{children}</Heading>,
          h3: ({ children }) => <Heading level={3}>{children}</Heading>,
          h4: ({ children }) => <Heading level={4}>{children}</Heading>,
          h5: ({ children }) => <Heading level={5}>{children}</Heading>,
          h6: ({ children }) => <Heading level={6}>{children}</Heading>,
          em: ({ children }) => <Text isItalic>{children}</Text>,
          strong: ({ children }) => <Text weight={styles.textStyle?.boldWeight}>{children}</Text>,
          ol: ({ node }) => (
            <ListWrapper>
              <List node={node as MarkdownNode} />
            </ListWrapper>
          ),
          ul: ({ node }) => (
            <ListWrapper>
              <List node={node as MarkdownNode} />
            </ListWrapper>
          ),
          li: () => null,
          table: ({ node }) => <Table node={node as MarkdownNode} />,
          tbody: () => null,
          td: () => null,
          th: () => null,
          thead: () => null,
          tr: () => null,
          img: ({ node }) => <InlineImage node={node as MarkdownNode} />,
          code: ({ children }) => <Text>{children}</Text>,
        }}
      >
        {i18nEnabled ? interpolateText(text, { withHyphenation: true }) : text}
      </ReactMarkdown>
    </MarkdownContext>
  )
}
